import styled, { css } from 'styled-components';

import { HOME, HOME_MID, HOME_NARROW, SPACE } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Text } from '../../../../components';

/**
 * Channel-card grid. On wide desktop each card spans a full row (one column) so
 * the figures sit on the left and the trend stretches out to the right; when
 * there's less room it falls back to the original two-up grid (≤1120px), then a
 * single stacked column (≤900px).
 */
export const Channels = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${SPACE[12]};

  ${HOME_NARROW} {
    grid-template-columns: 1fr 1fr;
  }

  ${HOME_MID} {
    grid-template-columns: 1fr;
  }

  /* A lone card (Affiliates, outside the "777" reveal) spans the two-up row rather than sitting half-width. */
  & > :only-child {
    grid-column: 1 / -1;
  }
`;

export const Card = styled.div<{ $locked?: boolean }>`
  background: ${HOME.panel};
  border-radius: 16px;
  padding: ${SPACE[12]};
  min-width: 0;
  /* Positioning context for the LockVeil (a locked card drops hoverWash, which would
     otherwise establish it). */
  position: relative;
  cursor: ${(p) => (p.$locked ? 'default' : 'pointer')};
  /* Locked: clip the frosted LockVeil to the rounded corners. Only when locked, so a
     live card's focus outline (which sits outside the box) is never clipped. */
  overflow: ${(p) => (p.$locked ? 'hidden' : 'visible')};
  /* ...and isolate the card's stacking so the veil (z-indexed above the card's own
     content) stays CONTAINED in the card. Without this the veil escapes to the page
     layer and paints over the header scrim, so a locked card near the top bleeds up
     into the header band instead of fading under it like every other tucked card. */
  ${(p) => (p.$locked ? 'isolation: isolate;' : '')}

  /* Hover/focus affordances only on a LIVE card — a locked card is inert (no wash, no
     chevron nudge, not focusable). */
  ${(p) =>
    p.$locked
      ? ''
      : css`
          ${hoverWash}

          &:hover .chev {
            color: ${HOME.t50};
            transform: translateX(2px);
          }
          &:focus-visible {
            outline: 2px solid ${HOME.t300};
            outline-offset: 2px;
          }
        `}
`;

export const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
`;

export const Lhs = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[16]};
  min-width: 0;
`;

export const IconSlot = styled.span`
  display: grid;
  place-items: center;
  color: ${HOME.t50};
`;

export const Name = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${HOME.t50};
`;

export const Chev = styled.span`
  display: grid;
  place-items: center;
  color: ${HOME.t50};
  transition: transform 0.18s ${HOME.ease}, color 0.18s ${HOME.ease};
`;

/**
 * Wraps the figures + trend. On wide desktop (full-row cards) they sit
 * side-by-side — the figures take a fixed left column and the chart fills the
 * rest of the row to the right; at ≤1120px the card reverts to the original
 * stacked layout (figures, then the chart underneath).
 */
export const Body = styled.div`
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  align-items: center;
  gap: ${SPACE[24]};
  margin-top: ${SPACE[12]};

  ${HOME_NARROW} {
    display: block;
  }
`;

export const Nums = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACE[16]};
`;

export const ChStat = styled.div`
  min-width: 0;
`;

export const ChNum = styled.div`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.15;
  color: ${HOME.t300};
  font-variant-numeric: tabular-nums;
`;

export const ChLabel = styled(Text)`
  display: block;
  font-size: 16px;
  font-weight: 300;
  line-height: 1.2;
  color: ${HOME.t100};
  margin-top: ${SPACE[4]};
  min-height: 2.4em;
`;

export const ChDelta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  row-gap: ${SPACE[4]};
  margin-top: 6px;
  flex-wrap: wrap;
`;

export const ChCap = styled(Text)`
  font-size: 13px;
  font-weight: 400;
  color: ${HOME.t600};
  white-space: nowrap;
`;

export const Chart = styled.div`
  min-width: 0;

  ${HOME_NARROW} {
    margin-top: ${SPACE[12]};
  }
`;

export const LegendWrap = styled.div`
  margin-top: ${SPACE[12]};
`;
